import { Box, BoxProps, ResponsiveContext, Text } from 'grommet';
import { Multimedia } from 'grommet-icons';
import { Project } from 'lib/api';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { shortenCurrency, USD } from 'src/utils/currency';
import { getAnimProps } from 'src/utils/styling';
import { AddNewButton } from '../AddNewButton';
import { Divider } from '../Divider';
import { SaveStatus, SaveStatusIndicator } from '../SaveStatusIndicator';

export type SessionEstimatorHeaderProps = {
  project: Project;
  onCreate: () => Promise<void>;
  saveStatus?: SaveStatus;
  totalWages?: number;
  grandTotal?: number;
  height?: BoxProps['height'];
};

export const SessionEstimatorHeader = observer(
  ({ project, onCreate, saveStatus, totalWages, grandTotal, height = '92px' }: SessionEstimatorHeaderProps) => {
    /** Context **/
    const screenSize = useContext(ResponsiveContext);

    /** Computed **/
    const isDesktop = screenSize === 'large';

    /** Render **/
    return (
      <Box
        background="light-3"
        flex="grow"
        direction="row"
        align="center"
        justify="between"
        height={height}
        gap="medium"
        style={{ zIndex: 1 }}
        elevation="small"
      >
        <Box direction="row" width="67%" gap="large" align="center">
          <Box direction="column" gap="xsmall">
            <Box
              margin={{ left: 'medium' }}
              animation={getAnimProps(0, 'slideUp')}
              justify="start"
              direction="row"
              pad={{ right: 'none' }}
            >
              <Text truncate size="xlarge">
                {project.name} Sessions
              </Text>
            </Box>
            <Divider height="2px" animation={{ type: 'slideRight', size: 'xlarge' }} />
            {saveStatus && (
              <Box align="end" animation={getAnimProps(0, 'slideDown')} pad={{ right: 'none' }}>
                <SaveStatusIndicator saveStatus={saveStatus} boxProps={{ margin: 'none' }} />
              </Box>
            )}
          </Box>

          <AddNewButton
            onClick={onCreate}
            style={{
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
            label="New Session"
            icon={<Multimedia size="1.2rem" />}
          />
          <Box flex />
        </Box>

        <Box direction="row" width="32%">
          <Box direction="column" gap="xsmall" justify="end" align="start" width="50%">
            <Box animation={getAnimProps(100, 'slideUp', 500)}>
              <Text size="small" weight={500}>
                TOTAL WAGES
              </Text>
            </Box>
            <Divider animation={{ type: 'slideLeft', size: 'xlarge', duration: 500 }} />
            <Box animation={getAnimProps(100, 'slideDown', 500)}>
              <Text truncate size="large" weight={500}>
                {(!isDesktop ? shortenCurrency : USD)(totalWages)}
              </Text>
            </Box>
          </Box>
          <Box direction="column" gap="xsmall" justify="end" align="start" width="50%">
            <Box animation={getAnimProps(0, 'slideUp', 500)}>
              <Text size="small" weight={700}>
                GRAND TOTAL
              </Text>
            </Box>
            <Divider animation={{ type: 'slideLeft', size: 'xlarge', duration: 500 }} />
            <Box animation={getAnimProps(0, 'slideDown', 500)}>
              <Text truncate size="large" weight={700}>
                {(!isDesktop ? shortenCurrency : USD)(grandTotal)}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);
